import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useSyncExternalStore,
} from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "drc-theme";
const VALID = ["light", "dark", "system"];

function getStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(v) ? v : "system";
  } catch {
    return "system";
  }
}

// Subscribe to system color-scheme changes
const subscribeMedia = (callback) => {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  if (mql.addEventListener) mql.addEventListener("change", callback);
  else mql.addListener(callback);
  return () => {
    if (mql.removeEventListener) mql.removeEventListener("change", callback);
    else mql.removeListener(callback);
  };
};

const getSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const getServerSystemTheme = () => "dark";

function applyClass(theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  } else {
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  }
}

export const ThemeProvider = ({ children }) => {
  const [preference, setPreference] = useState(() =>
    typeof window === "undefined" ? "system" : getStored()
  );

  const systemTheme = useSyncExternalStore(
    subscribeMedia,
    getSystemTheme,
    getServerSystemTheme
  );

  const applied = preference === "system" ? systemTheme : preference;

  useEffect(() => {
    applyClass(applied);
  }, [applied]);

  const setTheme = useCallback((pref) => {
    if (!VALID.includes(pref)) return;
    setPreference(pref);
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      /* ignore */
    }
  }, []);

  const value = { preference, applied, setTheme };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};
